import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { SlidersHorizontal, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const inputCls = "h-9 w-full rounded-lg border border-[#E9DDCC] bg-white px-2.5 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]";
const lblCls = "text-xs text-[#7A6455] font-medium";
const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;

const PLANS = [
  { id: 'essentielle', label: 'Essentielle' },
  { id: 'evolution', label: 'Évolution' },
  { id: 'transformation', label: 'Transformation' },
  { id: 'single', label: "Cours à l'unité" },
  { id: 'prive', label: 'Cours privé (/h)' },
];

export default function StudioConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [derived, setDerived] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/studio-config`, { withCredentials: true });
      setCfg(res.data.config);
      setDerived(res.data.derived);
    } catch (e) {
      toast.error('Impossible de charger les paramètres du studio.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setTop = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setPrice = (pid, v) => setCfg((c) => ({ ...c, prices: { ...c.prices, [pid]: v } }));
  const setFounder = (k, v) => setCfg((c) => ({ ...c, founder: { ...c.founder, [k]: v } }));
  const setSat = (k, v) => setCfg((c) => ({ ...c, saturation: { ...c.saturation, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        reformers: parseInt(cfg.reformers, 10),
        monthly_hours: parseInt(cfg.monthly_hours, 10),
        goal_confirmed_clients: parseInt(cfg.goal_confirmed_clients, 10),
        prices: Object.fromEntries(PLANS.map((p) => [p.id, parseFloat(cfg.prices[p.id])])),
        founder: {
          first_year_percent: parseFloat(cfg.founder.first_year_percent),
          first_year_months: parseInt(cfg.founder.first_year_months, 10),
          after_percent: parseFloat(cfg.founder.after_percent),
        },
        saturation: {
          warning: parseFloat(cfg.saturation.warning),
          critical: parseFloat(cfg.saturation.critical),
        },
      };
      const res = await axios.put(`${API}/admin/studio-config`, payload, { withCredentials: true });
      setCfg(res.data.config);
      setDerived(res.data.derived);
      toast.success('Paramètres enregistrés — dashboard, rapport et projections recalculés.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Valeurs invalides, vérifiez votre saisie.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const realSeats = (parseInt(cfg.monthly_hours, 10) || 0) * (parseInt(cfg.reformers, 10) || 0);

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="studio-config-panel">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <SlidersHorizontal className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Paramètres du studio</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Valeurs pilotant tout le tableau de bord</h3>
          </div>
          <Button onClick={save} disabled={saving}
            className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full h-9 px-5 text-sm"
            data-testid="config-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer
          </Button>
        </div>

        {/* Capacité & objectif */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Capacité & objectif</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className="block"><span className={lblCls}>Nombre de Reformer</span>
              <input type="number" min="1" max="50" value={cfg.reformers}
                onChange={(e) => setTop('reformers', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-reformers" /></label>
            <label className="block"><span className={lblCls}>Heures de salle / mois</span>
              <input type="number" min="1" max="744" value={cfg.monthly_hours}
                onChange={(e) => setTop('monthly_hours', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-hours" /></label>
            <label className="block"><span className={lblCls}>Objectif clientes confirmées</span>
              <input type="number" min="1" max="1000" value={cfg.goal_confirmed_clients}
                onChange={(e) => setTop('goal_confirmed_clients', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-goal" /></label>
          </div>
          <p className="text-[11px] text-[#AE9885] italic mt-2">
            Capacité réelle = {cfg.monthly_hours || 0} h × {cfg.reformers || 0} Reformer = <strong>{realSeats} places/mois</strong>.
          </p>
        </div>

        {/* Tarifs */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Tarifs des formules (€, TTC)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {PLANS.map((p) => (
              <label key={p.id} className="block"><span className={lblCls}>{p.label}</span>
                <input type="number" min="1" step="1" value={cfg.prices[p.id]}
                  onChange={(e) => setPrice(p.id, e.target.value)} className={`${inputCls} mt-1`} data-testid={`config-price-${p.id}`} /></label>
            ))}
          </div>
          <p className="text-[11px] text-[#AE9885] italic mt-2">
            Les tarifs pilotent dashboard, rapport et projections immédiatement. La facturation Stripe sera alignée au Bloc 3.
          </p>
        </div>

        {/* Remise fondatrice */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Remise fondatrice</div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className={lblCls}>1re année (%)</span>
              <input type="number" min="0" max="100" value={cfg.founder.first_year_percent}
                onChange={(e) => setFounder('first_year_percent', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-founder-y1" /></label>
            <label className="block"><span className={lblCls}>Durée (mois)</span>
              <input type="number" min="0" max="120" value={cfg.founder.first_year_months}
                onChange={(e) => setFounder('first_year_months', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-founder-months" /></label>
            <label className="block"><span className={lblCls}>Ensuite, à vie (%)</span>
              <input type="number" min="0" max="100" value={cfg.founder.after_percent}
                onChange={(e) => setFounder('after_percent', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-founder-after" /></label>
          </div>
          {derived?.founder_grids && (
            <div className="mt-3 overflow-x-auto rounded-xl border border-[#E9DDCC]">
              <table className="w-full text-xs" data-testid="config-founder-grid">
                <thead className="bg-[#F5EFE5] text-[#7A6455]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Formule</th>
                    <th className="text-right px-3 py-2 font-medium">Plein tarif</th>
                    <th className="text-right px-3 py-2 font-medium">Fondatrice 1re année</th>
                    <th className="text-right px-3 py-2 font-medium">Fondatrice à vie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EFE4D0]">
                  {Object.entries(derived.founder_grids).map(([pid, g]) => (
                    <tr key={pid}>
                      <td className="px-3 py-2 text-[#493329]">{g.name}</td>
                      <td className="px-3 py-2 text-right">{eur(g.std)}</td>
                      <td className="px-3 py-2 text-right text-[#C99A55]">{eur(g.founder_year1)}</td>
                      <td className="px-3 py-2 text-right text-[#9A744C]">{eur(g.founder_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-[#AE9885] italic px-3 py-2">Grille calculée automatiquement — enregistrez pour la figer (recalcul après sauvegarde).</p>
            </div>
          )}
        </div>

        {/* Seuils d'alerte */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Seuils d'alerte de capacité (%)</div>
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <label className="block"><span className={lblCls}>Surveillance (orange)</span>
              <input type="number" min="1" max="100" value={cfg.saturation.warning}
                onChange={(e) => setSat('warning', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-sat-warning" /></label>
            <label className="block"><span className={lblCls}>Critique (rouge)</span>
              <input type="number" min="1" max="100" value={cfg.saturation.critical}
                onChange={(e) => setSat('critical', e.target.value)} className={`${inputCls} mt-1`} data-testid="config-sat-critical" /></label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
